/**
 * A load is picked one customer at a time: the picker chooses a customer and
 * sees only that customer's trees, every order of theirs on the load combined,
 * in walk order. Picking for one customer and then the next keeps their trees
 * together on the trolley instead of mixed with everyone else's.
 *
 * With several pickers on one load, each holds the customer they are on
 * (a claim, from the server). Nobody else can pick for that customer until
 * the hold lapses or they take it over on purpose.
 */

import type { PickClaim, PickItem } from '@/types/nursery';

export interface PickCustomer {
  /** The server's customer key; goes in the URL as `?customer=` and names the claim. */
  key: string;
  name: string;
  code: string | null;
  order_numbers: string[];
  items: PickItem[];
  /** Items picked or marked short. */
  resolved: number;
  trees: number;
  picked_trees: number;
  /** Earliest collection booked across their orders, for a collections load. */
  collection_at: string | null;
  stop_sequence: number | null;
  /** Who is picking for them now, if anyone. */
  claim: PickClaim | null;
}

/**
 * Who an item is for. The server sends the key it claims by. Without one (a
 * server from before claims) it is worked out the same way, falling back to
 * the name when no ids were sent either.
 */
export function customerKey(it: PickItem): string {
  if (it.customer_key) return it.customer_key;
  if (it.customer_id != null) return `c${it.customer_id}`;
  if (it.customer_org_id != null) return `o${it.customer_org_id}`;
  if (it.customer_name) return `n:${it.customer_name}`;
  return `order${it.order_id}`;
}

const cmpNullLast = (a: number | string | null | undefined, b: number | string | null | undefined) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
};

/**
 * Walk order: stock at the despatch farm first, then block (by the farm's
 * block sequence, then name), row, side (no side before left before right),
 * position. Ties keep the server's order.
 */
export function walkOrder(items: PickItem[]): PickItem[] {
  const SIDE: Record<string, number> = { left: 1, right: 2 };
  return items
    .map((it, i) => ({ it, i }))
    .sort((x, y) =>
      Number(y.it.at_despatch_farm) - Number(x.it.at_despatch_farm)
      || cmpNullLast(x.it.block_sort_order, y.it.block_sort_order)
      || cmpNullLast(x.it.block_name, y.it.block_name)
      || cmpNullLast(x.it.row_number, y.it.row_number)
      || (x.it.row_side ? SIDE[x.it.row_side] : 0) - (y.it.row_side ? SIDE[y.it.row_side] : 0)
      || cmpNullLast(x.it.sequence_number, y.it.sequence_number)
      || cmpNullLast(x.it.location_name, y.it.location_name)
      || x.i - y.i)
    .map(({ it }) => it);
}

export const customerDone = (c: PickCustomer) => c.items.length > 0 && c.resolved >= c.items.length;

/** Someone other than this user is picking for them. */
export const heldByOther = (c: PickCustomer) => !!c.claim && !c.claim.mine;

/**
 * The customers on a load. A collections load lists them by earliest booking
 * (the person at the counter was booked first); any other load by stop, then
 * name. Customers someone else is picking for come after the free ones, and
 * customers with everything picked go to the bottom.
 */
export function pickCustomers(items: PickItem[], collection: boolean, claims: PickClaim[] = []): PickCustomer[] {
  const byKey = new Map<string, PickCustomer>();
  for (const it of items) {
    const key = customerKey(it);
    let c = byKey.get(key);
    if (!c) {
      c = {
        key, name: it.customer_name ?? it.order_number, code: it.customer_code ?? null,
        order_numbers: [], items: [], resolved: 0, trees: 0, picked_trees: 0,
        collection_at: null, stop_sequence: null,
        claim: claims.find((cl) => cl.customer_key === key) ?? null,
      };
      byKey.set(key, c);
    }
    c.items.push(it);
    if (!c.order_numbers.includes(it.order_number)) c.order_numbers.push(it.order_number);
    if (it.pick) { c.resolved += 1; c.picked_trees += it.pick.quantity_picked; }
    c.trees += it.quantity;
    if (it.collection_at && (!c.collection_at || it.collection_at < c.collection_at)) c.collection_at = it.collection_at;
    if (it.stop_sequence != null && (c.stop_sequence == null || it.stop_sequence < c.stop_sequence)) c.stop_sequence = it.stop_sequence;
  }
  const out = [...byKey.values()];
  for (const c of out) c.items = walkOrder(c.items);
  return out.sort((a, b) =>
    Number(customerDone(a)) - Number(customerDone(b))
    || Number(heldByOther(a)) - Number(heldByOther(b))
    || (collection ? cmpNullLast(a.collection_at, b.collection_at) : cmpNullLast(a.stop_sequence, b.stop_sequence))
    || a.name.localeCompare(b.name));
}
